import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { GalleryBlock as ReactivatedGalleryBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    baseVariantSelector,
    rootProductSelector,
} from "../../apps/configurator/selectors";
import { IOptionValues, IProduct } from "../../models/catalogue";
import { isoProductID } from "../../models/nominals";
import { useAppSelector } from "../../store/reducers";
import { loadWistiaPlayer } from "../../utils/wistia";
import { getSpacingClasses } from "../_spacing/spacing";
import { Gallery, IGalleryData } from "./Gallery";

type IGalleryBlockProps = Immutable<{
    blockData: ReactivatedGalleryBlock;
    defaultThumbnail: string;
    initialGalleryIndex?: number | null;
}>;

type IAttributePair = Immutable<{
    code: string | null;
    value: string | null;
}>;

const attributesInclude = (
    attributes: IOptionValues,
    attributeVal: Immutable<IAttributePair>,
): boolean => {
    return !!Object.entries(attributes).find(([c, v]) => {
        return c === attributeVal.code && v === attributeVal.value;
    });
};

const attributesMatch = (
    attributes: IOptionValues,
    galleryAttributes: Immutable<ReadonlyArray<IAttributePair>>,
): boolean => {
    return galleryAttributes.every((v) => attributesInclude(attributes, v));
};

const findMatchingGallery = (
    block: Immutable<ReactivatedGalleryBlock>,
    optionValues: IOptionValues,
    selectedRootProduct: IProduct | null,
    baseVariant: IProduct | null,
): Immutable<IGalleryData> | null => {
    const galleries = block.galleries ?? [];
    // default galleries have no product or attribute set
    const defaultGallery = galleries.find((gallery) => {
        if (!gallery.product && !gallery.attributes?.length) {
            return true;
        }
        return false;
    });

    const matchtAttributesGallery = galleries.find((gallery) => {
        // only match on galleries with attributes selection and no product selection
        if (
            gallery.product || // don't match if product also selected
            !gallery.attributes?.length ||
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        // all selected gallery variants match
        const variantMatch = attributesMatch(optionValues, gallery.attributes);
        return variantMatch;
    });

    const productMatchGallery = galleries.find((gallery) => {
        // only match on product selection but no attributes selection
        if (
            gallery.attributes?.length || // don't match if attributes also selected
            !gallery.product ||
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        const isRootOrVariant =
            gallery.product === isoProductID.unwrap(selectedRootProduct.id) ||
            gallery.product === isoProductID.unwrap(baseVariant.id);
        return isRootOrVariant;
    });

    const matchProductAndAttributesGallery = galleries.find((gallery) => {
        if (
            !gallery.attributes?.length || // attributes must be selected
            !gallery.product || // gallery must be selected
            !selectedRootProduct ||
            !baseVariant
        ) {
            return false;
        }
        const isRootOrVariant =
            gallery.product === isoProductID.unwrap(selectedRootProduct.id) ||
            gallery.product === isoProductID.unwrap(baseVariant.id);
        // all selected gallery variants match
        const variantMatch = attributesMatch(optionValues, gallery.attributes);
        return isRootOrVariant && variantMatch;
    });

    if (matchProductAndAttributesGallery) {
        return matchProductAndAttributesGallery;
    } else if (productMatchGallery) {
        return productMatchGallery;
    } else if (matchtAttributesGallery) {
        return matchtAttributesGallery;
    } else if (defaultGallery) {
        return defaultGallery;
    }
    // reset to no gallery
    return null;
};

export const GalleryBlock = (props: IGalleryBlockProps): React.ReactNode => {
    const optionValues: IOptionValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );
    const selectedRootProduct: IProduct | null = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );
    const baseVariant: IProduct | null = useAppSelector((state) =>
        baseVariantSelector(state.configurator),
    );

    const block = props.blockData;

    React.useEffect(() => {
        void loadWistiaPlayer();
    }, []);

    const spacingClasses = block.spacing
        ? getSpacingClasses(block.spacing)
        : "";
    const visibilityClass = block.responsive_visibility?.visibility ?? "";

    const renderGallery = (gallery: Immutable<IGalleryData>) => (
        <Gallery gallery={gallery} defaultThumbnail={props.defaultThumbnail} />
    );

    let content: React.ReactNode = null;

    // When the configurator Redux state hasn't loaded yet (SSR + initial hydration),
    // use the server-computed gallery index for a correct initial render.
    const blockGalleries = block.galleries ?? [];
    if (selectedRootProduct === null && props.initialGalleryIndex != null) {
        const ssrGallery = blockGalleries[props.initialGalleryIndex];
        if (ssrGallery) {
            content = renderGallery(ssrGallery);
        }
    }

    if (!content) {
        // Normal Redux-driven gallery matching
        const gallery = findMatchingGallery(
            block,
            optionValues,
            selectedRootProduct,
            baseVariant,
        );
        if (gallery) {
            content = renderGallery(gallery);
        }
    }

    if (!content) {
        return null;
    }

    return (
        <section
            className={concatClassNames([
                "gallery-block",
                spacingClasses,
                visibilityClass,
            ])}
            aria-label={t`Gallery Carousel`}
        >
            {content}
        </section>
    );
};
